import {Component, Inject} from '@angular/core';
import {FormControl, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import { ConsoleAction } from 'src/types/d3ePolicy';

export interface IActionConfirmationDialogData {
    action: ConsoleAction;
    title: string;
    content: string;

    endpointId?: string;
    model?: string;
    product?: string;
    scope?: string;
    execution?: string;

    showEndpointBox?: boolean;
    endpointPrompt?: string;

    showAcknowledgeCheck?: boolean;
    acknowledgeCheckPrompt?: string;

    showDate?: boolean;
    datePrompt?: string;

    showRanges?: boolean;
    rangePrompt?: string;

    acceptBtnName?: string;
    rejectBtnName?: string;
}

type ActionDialogConfig =
    Pick<IActionConfirmationDialogData, 'title' | 'content'> &
    Partial<Omit<IActionConfirmationDialogData, 'action' | 'title' | 'content'>>;

export const ENDPOINT_ACTION_DIALOGS:
    Partial<Record<ConsoleAction, ActionDialogConfig>> = {

    [ConsoleAction.DEACTIVATE_PBA]: {
        title: 'Deactivate PBA',
        content: 'Deactivate PBA on this endpoint?',
        product: 'PBA',
        showEndpointBox: true,
        endpointPrompt: 'Enter the Endpoint ID to confirm:',
        acceptBtnName: 'Deactivate',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REACTIVATE_PBA]: {
        title: 'Reactivate PBA',
        content: 'Reactivate PBA on this endpoint?',
        product: 'PBA',
        acceptBtnName: 'Reactivate',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.LOCK_PBA_RANGES]: {
        title: 'Lock PBA Protected Ranges',
        content: 'Lock the selected PBA protected ranges?',
        product: 'PBA',
        showRanges: true,
        rangePrompt: 'Select protected ranges to lock:',
        acceptBtnName: 'Lock',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.UNLOCK_PBA_RANGES]: {
        title: 'Unlock PBA Protected Ranges',
        content: 'Unlock the selected PBA protected ranges?',
        product: 'PBA',
        showRanges: true,
        rangePrompt: 'Select protected ranges to unlock:',
        acceptBtnName: 'Unlock',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.ERASE_PBA]: {
        title: 'Erase Drive (PBA)',
        content: 'Permanently erase this drive?',
        product: 'PBA',
        showEndpointBox: true,
        endpointPrompt: 'Enter the Endpoint ID to confirm:',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that this action permanently erases the drive.',
        acceptBtnName: 'Erase Drive',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.SET_PBA_ERASE_AFTER]: {
        title: 'Set Erase-After Date (PBA)',
        content: 'Set an erase-after date for this drive.',
        product: 'PBA',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that this action permanently erases the drive after the set date.',
        showDate: true,
        datePrompt: 'Erase after:',
        acceptBtnName: 'Set Date',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.CLEAR_PBA_ERASE_AFTER]: {
        title: 'Clear Erase-After Date (PBA)',
        content: 'Clear the erase-after date for this drive?',
        product: 'PBA',
        acceptBtnName: 'Clear Date',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REMOVE_ENROLLCODES_PBA]: {
        title: 'Remove PBA Enrollment Codes',
        content: 'Remove all PBA enrollment codes from the selected endpoint?',
        product: 'PBA',
        acceptBtnName: 'Remove',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.DEACTIVATE_FDE]: {
        title: 'Deactivate FDE',
        content: 'Deactivate FDE on this endpoint?',
        product: 'FDE',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that FDE protection will be deactivated.',
        showEndpointBox: true,
        endpointPrompt: 'Enter the Endpoint ID to confirm:',
        acceptBtnName: 'Deactivate',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REACTIVATE_FDE]: {
        title: 'Reactivate FDE',
        content: 'Reactivate FDE on this endpoint?',
        product: 'FDE',
        acceptBtnName: 'Reactivate',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.ERASE_FDE]: {
        title: 'Erase Drive (FDE)',
        content: 'Permanently erase this drive?',
        product: 'FDE',
        showEndpointBox: true,
        endpointPrompt: 'Enter the Endpoint ID to confirm:',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that this action permanently erases the drive.',
        acceptBtnName: 'Erase Drive',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REMOVE_ENROLLCODES_FDE]: {
        title: 'Remove FDE Enrollment Codes',
        content: 'Remove all FDE enrollment codes from the selected endpoint?',
        product: 'FDE',
        acceptBtnName: 'Remove',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REMOVE_PBA_USER]: {
        title: 'Remove PBA User',
        content: 'Are you sure you want to remove the selected PBA user(s)?',
        product: 'PBA',
        acceptBtnName: 'Remove',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.REMOVE_FDE_USER]: {
        title: 'Remove FDE User',
        content: 'Are you sure you want to remove the selected FDE user(s)?',
        product: 'FDE',
        acceptBtnName: 'Remove',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.CANCEL_PBA_USER_ACTION]: {
        title: 'Clear Pending User Action',
        content: 'Remove pending action not yet sent to the agent?',
        product: 'PBA',
        acceptBtnName: 'Clear Action',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.CANCEL_FDE_USER_ACTION]: {
        title: 'Clear Pending User Action',
        content: 'Remove pending action not yet sent to the agent?',
        product: 'FDE',
        acceptBtnName: 'Clear Action',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.CLEAR_ALL_PBA_ACTIONS]: {
        title: 'Clear All Pending Actions',
        content: 'Clear all currently pending actions not sent to the agent?',
        product: 'PBA',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that all pending actions will be removed.',
        acceptBtnName: 'Clear Actions',
        rejectBtnName: 'Cancel',
    },

    [ConsoleAction.CLEAR_ALL_FDE_ACTIONS]: {
        title: 'Clear All Pending Actions',
        content: 'Clear all currently pending actions not sent to the agent?',
        product: 'FDE',
        showAcknowledgeCheck: true,
        acknowledgeCheckPrompt:
            'I understand that all pending actions will be removed.',
        acceptBtnName: 'Clear Actions',
        rejectBtnName: 'Cancel',
    },
};

//TODO: Remove once combined with the below request
export interface IDeleteEnrollCodesRequest {
    type: 'pba' | 'fde';
    diskSerials: string[];
}

export interface IAgentActionRequest {
    action: ConsoleAction;
    date?: string;
    selectedRanges?: string[];
    //TODO: Not sure if both
    endpointIds: string[];
    diskSerials: string[];
}

export interface IActionConfirmationDialogResult {
    confirmed: boolean;
    action: ConsoleAction;
    date?: string;
    range?: number;
}

export function getActionDialogData(
    action: ConsoleAction,
    endpointId: string,
    model: string,
): IActionConfirmationDialogData {
    const config = ENDPOINT_ACTION_DIALOGS[action];

    if (!config) {
        throw new Error(`No dialog configuration for action: ${action}`);
    }

    return {
        action,
        ...config,
        endpointId,
        model,
    };
}

@Component({
    selector: 'app-action-confirmation-dialog',
    templateUrl: './action-confirmation-dialog.component.html',
    styleUrls: ['./action-confirmation-dialog.component.scss'],
    standalone: false,
})
export class ActionConfirmationDialogComponent {
    endpointIdControl = new FormControl('');
    acknowledgeControl = new FormControl(false);
    dateControl = new FormControl('');
    rangeControl = new FormControl<number | null>(
        null,
        [
            Validators.required,
            Validators.min(0),
        ]
    );

    constructor(
        public readonly dialogRef: MatDialogRef<
            ActionConfirmationDialogComponent,
            IActionConfirmationDialogResult
        >,
        @Inject(MAT_DIALOG_DATA)
        public data: IActionConfirmationDialogData,
    ) {}

    get endpointMatches(): boolean {
        if (!this.data.showEndpointBox) {
            return true;
        }

        return (
            this.endpointIdControl.value?.trim() ===
            this.data.endpointId
        );
    }

    get acknowledged(): boolean {
        if (!this.data.showAcknowledgeCheck) {
            return true;
        }

        return this.acknowledgeControl.value === true;
    }

    get canConfirm(): boolean {
        return this.endpointMatches &&
            this.acknowledged &&
            (!this.data.showRanges || this.rangeControl.valid);
    }

    confirm(): void {
        if (!this.canConfirm) {
            if (!this.endpointMatches) {
                this.endpointIdControl.setErrors({
                    endpointMismatch: true,
                });
            }

            return;
        }

        if(this.data.action == ConsoleAction.CLEAR_PBA_ERASE_AFTER) {
            this.data.showDate
        }

        this.dialogRef.close({
            action: this.data.action,
            confirmed: true,
            date: this.data.action === ConsoleAction.CLEAR_PBA_ERASE_AFTER
                ? '0'
                : this.data.showDate
                    ? this.dateControl.value || undefined
                    : undefined,
            range: this.data.showRanges
                ? this.rangeControl.value ?? undefined
                : undefined,
        });
    }

    cancel(): void {
        this.dialogRef.close({
            confirmed: false,
            action: this.data.action,
        });
    }
}

export { ConsoleAction };
